import { useMutation } from '@apollo/client';
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LotContext } from '../../../../context/LotContext';
import { DeleteLotResponse, DeleteLotVariables, LotMutations } from '../../../../graphql/lot.graphql';
import { LotInput } from '../../../../types/lot';
import { DeleteLotInput } from '../../../../types/lot/lot.input';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { t } from '../../../../types/translation/Translator';
import { testIds } from '../../../../util/identifiers/identifiers.util';
import { DeleteModal } from '../../../../VentoryUI/components/common/Modal';

interface DeleteLotModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  idsToDelete: Set<string>;
}

export default function DeleteLotModal({ open, setOpen, idsToDelete }: DeleteLotModalProps) {
  const navigate = useNavigate();
  const { lots, setLots } = useContext(LotContext);

  const [error, setError] = useState<string>('');

  const [remove, { loading }] = useMutation<DeleteLotResponse, DeleteLotVariables>(LotMutations.remove, {
    onCompleted: res => {
      res.deleteLot.forEach(lot => lots.delete(lot.id));
      setLots(new Map(lots));
      handleClose();
    },
    onError: err => setError(err.message),
  });

  const handleDelete = async () => {
    try {
      const deletedLot: DeleteLotInput[] = [];
      for (const id of idsToDelete) {
        const lot = lots.get(id);
        if (lot) deletedLot.push(new LotInput(lot).forDelete());
      }

      let deleted = 0;
      do {
        await remove({
          variables: {
            lots: deletedLot.slice(deleted, deleted + 3000),
          },
        });
        deleted += 3000;
      } while (deleted < deletedLot.length);
      navigate('/stock/lots');
    } catch (e) {
      setError(String(e));
    }
  };

  const handleClose = () => {
    setError('');
    setOpen(false);
  };

  return (
    <DeleteModal
      testId={testIds.deleteLotModal}
      onClose={handleClose}
      open={open}
      error={error}
      text={t(TranslationKey.verifyDeleteLots).singular.label}
      onConfirm={handleDelete}
      loading={loading}
    />
  );
}
